import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Dashboard = ({ user }) => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [bookingsPerPage] = useState(10);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const currentUser = user || JSON.parse(localStorage.getItem('user'));
    if (!currentUser) {
      navigate('/user/login');
      return;
    }

    const fetchBookings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/user/bookings`, {
          params: { userId: currentUser._id, status: filter === 'all' ? undefined : filter }
        });
        setBookings(response.data.bookings);
        setFilteredBookings(response.data.bookings);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch bookings');
        console.error('Error fetching bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user, filter, navigate]);

  // Apply search filter whenever searchTerm or bookings change
  useEffect(() => {
    const filtered = bookings.filter(booking => {
      return (
        booking._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.vehicleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.pickupLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.dropoffLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.totalAmount.toString().includes(searchTerm)
      );
    });
    setFilteredBookings(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, bookings]);

  // Pagination logic
  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking);
  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/user/login');
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'badge bg-warning text-dark',
      confirmed: 'badge bg-primary',
      assigned: 'badge bg-info',
      completed: 'badge bg-success',
      cancelled: 'badge bg-danger'
    };
    return <span className={statusClasses[status]}>{status}</span>;
  };

  const handleDownloadInvoice = (invoiceUrl) => {
    window.open(invoiceUrl, '_blank');
  };

  const currentUser = user || JSON.parse(localStorage.getItem('user'));

  if (!currentUser) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="user-dashboard-container container mt-4">
      <div className="dashboard-header d-flex justify-content-between align-items-center mb-4">
        <h2>Welcome, {currentUser.name}</h2>
        <button onClick={handleLogout} className="btn btn-outline-danger">Logout</button>
      </div>

      <div className="booking-filters mb-4">
        <div className="row">
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="btn btn-outline-secondary" type="button">
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>
          <div className="col-md-6">
            <div className="btn-group flex-wrap">
              <button 
                className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('all')}
              >
                All Bookings
              </button>
              <button 
                className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('pending')}
              >
                Pending
              </button>
              <button 
                className={`btn ${filter === 'confirmed' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('confirmed')}
              >
                Confirmed
              </button>
              <button 
                className={`btn ${filter === 'assigned' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('assigned')}
              >
                Assigned
              </button>
              <button 
                className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('completed')}
              >
                Completed
              </button>
              <button 
                className={`btn ${filter === 'cancelled' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('cancelled')}
              >
                Cancelled
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="booking-list">
          {currentBookings.length === 0 ? (
            <div className="alert alert-info">No bookings found</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Booking ID</th>
                      <th>Vehicle</th>
                      <th>Pickup Date</th>
                      <th>Pickup Location</th>
                      <th>Dropoff Location</th>
                      <th>Total Amount</th>
                      <th>Status</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBookings.map((booking) => (
                      <tr key={booking._id}>
                        <td>{booking._id.substring(0, 8)}</td>
                        <td>{booking.vehicleName}</td>
                        <td>{formatDate(booking.pickupDate)}</td>
                        <td>{booking.pickupLocation}</td>
                        <td>{booking.dropoffLocation}</td>
                        <td>${booking.totalAmount.toFixed(2)}</td>
                        <td>{getStatusBadge(booking.bookingStatus)}</td>
                        <td>
                          {booking.invoice && (
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => handleDownloadInvoice(booking.invoice)}
                            >
                              <i className="bi bi-download me-1"></i>
                              Download
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <nav className="mt-4">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {Array.from({ length: totalPages }, (_, i) => {
                    // Show limited page numbers with ellipsis
                    if (
                      i === 0 || 
                      i === totalPages - 1 || 
                      (i >= currentPage - 2 && i <= currentPage + 2)
                    ) {
                      return (
                        <li 
                          key={i + 1} 
                          className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => paginate(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      );
                    } else if (
                      (i === 1 && currentPage > 4) || 
                      (i === totalPages - 2 && currentPage < totalPages - 3)
                    ) {
                      return (
                        <li key={i + 1} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    return null;
                  })}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>

              <div className="text-center text-muted">
                Showing {indexOfFirstBooking + 1} to {Math.min(indexOfLastBooking, filteredBookings.length)} of {filteredBookings.length} bookings
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;